import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { tokens } from '@/theme'
import { pageWindow } from './pageWindow'

interface Props {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
}

/**
 * 1-indexed page navigator + record summary. Mirrors the API's pagination
 * envelope (`page`, `pageSize`, `total`).
 *
 * Numbered buttons rather than "page N of M": with a handful of pages, one
 * click reaches any of them. Past `MAX_NUMBERS` the numbers window around
 * the current page and the chevrons carry the rest.
 *
 * The summary line always renders — even for a single page — so the count
 * is visible on quiet lookups.
 */
export function Pagination({ page, pageSize, total, onPageChange }: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        marginTop: 24,
        fontFamily: tokens.fontDisplay,
        fontSize: 12,
        color: tokens.ink3,
      }}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {total === 0 ? 'No results' : `Showing ${start}–${end} of ${total}`}
      </span>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {page > 1 && <NavButton onClick={() => onPageChange(page - 1)} dir="prev" />}
          {pageWindow(page, totalPages).map(n => (
            <PageButton key={n} n={n} active={n === page} onClick={() => onPageChange(n)} />
          ))}
          {page < totalPages && <NavButton onClick={() => onPageChange(page + 1)} dir="next" />}
        </div>
      )}
    </div>
  )
}

function PageButton({ n, active, onClick }: { n: number; active: boolean; onClick: () => void }) {
  const [hov, setHov] = useState(false)
  return (
    <button
      onClick={onClick}
      aria-label={`Page ${n}`}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        background: active ? tokens.goldWash : hov ? tokens.paper2 : 'transparent',
        border: `1px solid ${active ? tokens.gold : tokens.line2}`,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
        fontSize: 12,
        fontWeight: active ? tokens.weightDemi : tokens.weightBook,
        fontVariantNumeric: 'tabular-nums',
        color: active ? tokens.goldDark : tokens.ink2,
        cursor: active ? 'default' : 'pointer',
        transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      {n}
    </button>
  )
}

function NavButton({ onClick, dir }: { onClick: () => void; dir: 'prev' | 'next' }) {
  const [hov, setHov] = useState(false)
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight
  return (
    <button
      onClick={onClick}
      aria-label={dir === 'prev' ? 'Previous page' : 'Next page'}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        background: hov ? tokens.paper2 : 'transparent',
        border: `1px solid ${tokens.line2}`,
        borderRadius: tokens.radiusMd,
        color: tokens.ink2,
        cursor: 'pointer',
        transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <Icon size={14} strokeWidth={1.8} />
    </button>
  )
}
